import { Colors, Metrics, Typography } from "../../resources";
import { Platform, StyleSheet, TextInput, UIManager, View } from "react-native";
import React, { useEffect, useState } from "react";

import SIcon from "./SIcon";
import { SIconButton } from "../molecules";
import { SIconType } from "../../types";
import { ifElse } from "../../utils";
import { noop } from "lodash";

const TEXTINPUT_ENTRY_WIDTH = "100%";

interface IProps {
  title?: string;
  type?: SIconType;
  value?: string | null;
  width?: string | number;
  placeholder: string;
  autoCapitalize?: "none" | "sentences" | "words" | "characters" | undefined;
  textStyle?: any;
  keyboardType?:
    | "default"
    | "email-address"
    | "numeric"
    | "phone-pad"
    | "visible-password"
    | "ascii-capable"
    | "numbers-and-punctuation"
    | "url"
    | "number-pad"
    | "name-phone-pad"
    | "decimal-pad"
    | "twitter"
    | "web-search"
    | undefined;
  enablesReturnKeyAutomatically?: boolean;
  accessoryDisabled?: boolean;
  accessoryIcon?: boolean;
  accessoryIconType?: SIconType;
  isError?: boolean;
  secureTextEntry?: boolean;
  returnKeyType?:
    | "none"
    | "default"
    | "next"
    | "search"
    | "done"
    | "go"
    | "send"
    | "previous"
    | "google"
    | "join"
    | "route"
    | "yahoo"
    | "emergency-call"
    | undefined;
  testID?: string;
  onSubmitEditing?: (value: {
    nativeEvent: { text: string; eventCount: number; target: string };
  }) => void;
  onChangeText?: (value: any) => void;
  onKeyPress?: (e: any) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  reference?: (value: any) => void;
  onAccessoryIconPress?: () => void;
}

const STextInput: React.FC<IProps> = ({
  type = SIconType.Blank,
  value = "",
  width = TEXTINPUT_ENTRY_WIDTH,
  placeholder = "",
  autoCapitalize = "none",
  textStyle = {},
  keyboardType = "default",
  enablesReturnKeyAutomatically = true,
  accessoryIconType = SIconType.Blank,
  accessoryIcon = false,
  isError = false,
  secureTextEntry = false,
  returnKeyType = "next",
  testID = "",
  onSubmitEditing = noop,
  onChangeText = noop,
  onKeyPress,
  onFocus = noop,
  onBlur = noop,
  reference = noop,
  onAccessoryIconPress = noop,
}) => {
  const [focus, setFocus] = useState(false);
  const [secureText] = useState(secureTextEntry);
  const [error, setError] = useState(isError);

  useEffect(() => {
    if (
      Platform.OS === "android" &&
      UIManager.setLayoutAnimationEnabledExperimental
    ) {
      UIManager.setLayoutAnimationEnabledExperimental(true);
    }
  }, []);

  useEffect(() => {
    setError(isError);
  }, [isError]);

  const onLocalBlur = () => {
    setFocus(false);
    onBlur();
  };

  const onLocalKeyPress = (e: any) => {
    if (e.key === "Escape" || e.key === "Return") {
      onLocalBlur();
    }
    onKeyPress && onKeyPress(e);
  };

  const renderAccessoryIcon = (_accessoryIcon: boolean) => {
    return _accessoryIcon ? (
      <SIconButton
        type={accessoryIconType}
        onPress={onAccessoryIconPress}
        size={20}
        isContainer
      />
    ) : (
      <View style={{ width: Metrics.padding8 }} />
    );
  };

  return (
    <View
      style={[
        styles.container,
        {
          width,
        },
      ]}
    >
      <View
        style={[
          styles.textInputContainer,
          {
            borderColor: error
              ? Colors.red400
              : ifElse(focus, Colors.blue400, Colors.grey300),
            borderWidth: 1,
            paddingHorizontal: accessoryIcon ? Metrics.padding8 : 0,
          },
        ]}
      >
        {accessoryIcon ? (
          <View style={styles.iconsContainer}>
            <SIcon
              type={type}
              color={Colors.blue700}
              size={Metrics.iconSmall}
            />
          </View>
        ) : (
          <View style={{ width: Metrics.padding8 }} />
        )}
        <TextInput
          ref={reference}
          style={[styles.textInput, textStyle]}
          placeholder={placeholder}
          placeholderTextColor={Colors.grey400}
          autoCapitalize={autoCapitalize}
          keyboardType={keyboardType}
          secureTextEntry={secureText}
          autoCorrect={false}
          value={value || ""}
          enablesReturnKeyAutomatically={enablesReturnKeyAutomatically}
          returnKeyType={returnKeyType}
          testID={testID}
          onChangeText={onChangeText}
          onKeyPress={onLocalKeyPress}
          onBlur={onLocalBlur}
          onFocus={() => {
            setFocus(true);
            setError(false);
            onFocus();
          }}
          onSubmitEditing={onSubmitEditing}
          editable={true}
        />
        {value !== "" ? (
          <View style={styles.iconsContainer}>
            {renderAccessoryIcon(accessoryIcon)}
          </View>
        ) : null}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    height: Metrics.textInputHeight,
    alignItems: "center",
    justifyContent: "center",
  },
  textInputContainer: {
    flexDirection: "row",
    alignItems: "center",
    width: TEXTINPUT_ENTRY_WIDTH,
    height: Metrics.textInputHeight,
    borderRadius: Metrics.borderRadiusStandard,
    borderWidth: 1,
  },
  textInput: {
    flex: 1,
    ...Typography.body1,
    textAlign: "left",
    color: Colors.grey900,
    outlineWidth: 0,
  },
  placeholder: {
    width: Metrics.iconSmall,
    height: Metrics.iconSmall,
  },
  iconsContainer: {
    marginLeft: Metrics.padding6,
    marginRight: Metrics.padding6,
  },
});

export default STextInput;
